import React, { useEffect, useState } from 'react';
import QuoteStore, { IPartList } from '../../stores/QuoteStore';
import { Badge, Button, Modal, notification, Radio, Table, Typography } from 'antd';
import Search from 'antd/lib/input/Search';
import { FileUnknownOutlined } from '@ant-design/icons';
import { ErrorRender, Price } from '@partsbadger/library';
import { observer } from 'mobx-react';
import ButtonAddPreviousQuotedParts from './components/ButtonAddPreviousQuotedParts';

interface Props {
    account_id: number;
}

const PreviouslyQuotedParts = (props: Props) => {
    const { account_id } = props;

    const [modalPreviousParts, setModalPreviousParts] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>();
    const [selectedRowKeys, setRowKeys] = useState([]);
    const [currentPage, setCurrentPage] = useState<number>(1);

    /**
     * Reset the selectedRowKeys in table-previous-parts.
     */
    const reloadTableRows = () => {
        setRowKeys([]);
    };

    /**
     * Captures the keys of the selected rows in table-previous-parts.
     * @param selectedRowKeys
     */
    const onSelectChange = selectedRowKeys => {
        setRowKeys(selectedRowKeys);
    };

    /**
     * Renders the checkboxes in the table-previous-parts.
     */
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    /**
     * Add existing products from other quotes to the current quote in process.
     */
    const add_existing_items = async () => {
        try {
            await QuoteStore.createLineItemFromExistingLineItems(selectedRowKeys);
            reloadTableRows();
            notification.success({ message: 'Success', description: `Items were added correctly.` });
        } catch (error: any) {
            notification.error({ message: 'Error', description: <ErrorRender error={error} /> });
        }
    };

    /**
     * Search for existing products in other quotes, related to the account of the quote in process in modal-previous-parts.
     */
    const onSearchPreviousPart = page => {
        setCurrentPage(page);
        const payload = {
            quote__stage: 'Completed',
            page: page,
            search: searchQuery,
        };

        QuoteStore.getAllByAccountId(account_id, payload);
    };

    useEffect(() => {
        QuoteStore.clean_previous_items();
        onSearchPreviousPart(1);
    }, []);

    return (
        <>
            <ButtonAddPreviousQuotedParts
                handleClickShowPreviousParts={() => {
                    setModalPreviousParts(true);
                }}
            />
            <Modal
                className={'modal-previous-parts'}
                title="Add Previously Quoted Parts"
                open={modalPreviousParts}
                closable={true}
                width="min(1250px, 90vw)"
                onCancel={() => {
                    reloadTableRows();
                    QuoteStore.clean_previous_items();
                    setModalPreviousParts(false);
                }}
                footer={false}
            >
                <div className={'flex flex-row pb-4'}>
                    <div className={'w-1/2 mx-4'} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div>
                            <span style={{ fontSize: 12, marginRight: 6 }}>Search by:</span>
                            <Radio.Group buttonStyle="solid" size="small">
                                <Radio.Button value={'parts'}>Quotes / Parts</Radio.Button>
                            </Radio.Group>
                        </div>
                        <Search
                            allowClear
                            onChange={event => setSearchQuery(event.target.value)}
                            onSearch={() => {
                                QuoteStore.clean_previous_items();
                                onSearchPreviousPart(1);
                            }}
                            placeholder="Search by quote name or part name..."
                            size="small"
                        />
                    </div>

                    <div className={'w-1/2 mx-4'}>
                        <Button
                            type={'primary'}
                            size={'small'}
                            disabled={selectedRowKeys.length <= 0}
                            onClick={() => add_existing_items()}
                        >
                            Add ({selectedRowKeys.length}) Parts
                        </Button>
                        <Button className={'mx-4'} size={'small'} type="default" onClick={reloadTableRows}>
                            Remove selected
                        </Button>
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                >
                    <span>{`Total Records: ${QuoteStore.part_list_response?.count ?? 0}`}</span>
                </div>
                <div>
                    <Table<IPartList>
                        className="table-previous-parts"
                        rowKey={record => String(record.id)}
                        rowSelection={rowSelection}
                        columns={[
                            {
                                title: 'Preview',
                                dataIndex: 'step_file',
                                render: (t, record: IPartList) => {
                                    if (record.master_product?.step_file && record.master_product?.step_file.image) {
                                        return (
                                            <div>
                                                <img
                                                    style={{ width: 60, cursor: 'pointer' }}
                                                    src={record.master_product?.step_file.image}
                                                />
                                            </div>
                                        );
                                    }
                                    return <FileUnknownOutlined style={{ fontSize: 40 }} />;
                                },
                            },
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                render: (_: string, record: IPartList) => (
                                    <>
                                        <div>{record.master_product?.name}</div>
                                        <div style={{ display: 'flex' }}>
                                            <span style={{ fontSize: 12 }}>
                                                {Number(record.master_product?.length).toFixed(3)}
                                                {record.master_product.unit} x{' '}
                                                {Number(record.master_product?.width).toFixed(3)}
                                                {record.master_product.unit} x{' '}
                                                {Number(record.master_product?.height).toFixed(3)}
                                                {record.master_product?.unit}
                                            </span>
                                        </div>
                                    </>
                                ),
                            },
                            {
                                title: 'Quote',
                                dataIndex: 'source',
                                render: (_: string, record: IPartList) => (
                                    <div style={{ textAlign: 'left' }}>
                                        <div>
                                            <Typography.Link
                                                href={`/quotes/${record.quote.id}/details/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {record.quote.name}
                                            </Typography.Link>
                                        </div>
                                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                            {new Date(record.created_time).toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#aaa' }}>{record.created_by.email}</div>
                                        <div>{record.quote?.source}</div>
                                        <div>
                                            {record.quote?.is_dynamic && (
                                                <Badge
                                                    count={'Dynamic'}
                                                    style={{
                                                        fontSize: 10,
                                                        height: 20,
                                                        backgroundColor: '#7576c4',
                                                    }}
                                                />
                                            )}
                                            {record.quote?.type === 'Requote' && (
                                                <Badge count={'Requote'} style={{ backgroundColor: '#8E44AD' }} />
                                            )}
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                title: 'Information',
                                dataIndex: 'information',
                                render: (_: string, record: IPartList) => (
                                    <>
                                        <div>
                                            <span>Material: </span>
                                            {record.material && record.material.name}
                                        </div>
                                        <div>
                                            <span>Coating: </span>
                                            {record.coating && record.coating.name}
                                        </div>
                                        <div>
                                            <span>Finish: </span>
                                            {record.finish && record.finish.name}
                                        </div>
                                    </>
                                ),
                            },
                            {
                                title: 'Unit Price',
                                dataIndex: 'unit_price',
                                render: (unit_price: number) => <Price value={unit_price} />,
                            },
                            {
                                title: 'Qty',
                                dataIndex: 'quantity',
                            },
                            {
                                title: 'Total Price',
                                dataIndex: 'total_price',
                                render: (total_price: number) => <Price value={total_price} />,
                            },
                            {
                                title: '',
                                key: 'action',
                                width: 90,
                                render: (_: any, record: IPartList) => (
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={async () => {
                                            try {
                                                await QuoteStore.createLineItemFromExistingLineItems([record.id]);
                                                reloadTableRows();
                                                notification.success({
                                                    message: 'Success',
                                                    description: 'Part added to the quote.',
                                                });
                                            } catch (error: any) {
                                                notification.error({
                                                    message: 'Error',
                                                    description: <ErrorRender error={error} />,
                                                });
                                            }
                                        }}
                                    >
                                        Add Part
                                    </Button>
                                ),
                            },
                        ]}
                        loading={QuoteStore.loading}
                        dataSource={QuoteStore.part_list_response?.results || []}
                        pagination={{
                            pageSize: 10,
                            current: currentPage,
                            pageSizeOptions: [],
                            total: QuoteStore.part_list_response?.count || 0,
                            onChange: page => {
                                onSearchPreviousPart(page);
                            },
                            defaultPageSize: 10,
                            showSizeChanger: false,
                        }}
                    />
                </div>
            </Modal>
        </>
    );
};

export default observer(PreviouslyQuotedParts);
